import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus, Search, Filter, Edit, Eye, Trash2, CheckCircle,
  Clock, AlertTriangle, Archive, MoreHorizontal, Calendar,
  MapPin, Users, Star, ExternalLink, FileText, ArrowLeft, Home
} from 'lucide-react';
import { cmsFetch } from '../lib/cmsApi';
import CMSAuth from '../components/CMSAuth';

const CMSWalkManagement = () => {
  const navigate = useNavigate();
  const [walks, setWalks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    walk_type: '',
    difficulty: '',
    region: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total_count: 0
  });
  const [selectedWalks, setSelectedWalks] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);

  useEffect(() => {
    if (hasAccess) fetchWalks();
  }, [hasAccess, filters, pagination.page]);

  const fetchWalks = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        skip: (pagination.page - 1) * pagination.per_page,
        limit: pagination.per_page,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      });

      const response = await cmsFetch(`/api/cms/walks?${params}`);

      if (response.ok) {
        const data = await response.json();
        setWalks(data.walks || []);
        setPagination(prev => ({
          ...prev,
          total_count: data.total_count || 0
        }));
      } else {
        const errText = await response.text();
        console.error('CMS walks fetch failed:', response.status, errText);
      }
    } catch (error) {
      console.error('Error fetching walks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (walkId, newStatus) => {
    try {
      const response = await cmsFetch(`/api/cms/walks/${walkId}/review?new_status=${newStatus}&review_notes=Status changed via CMS`, {
        method: 'POST',
      });

      if (response.ok) {
        fetchWalks(); // Refresh the list
        console.log(`Walk status changed to ${newStatus}`);
      } else {
        console.error('Failed to update status');
        alert('Failed to update status. Please try again.');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating status. Please try again.');
    }
  };

  const handleBulkOperation = async (operation, parameters = {}) => {
    if (selectedWalks.length === 0) return;

    try {
      const response = await cmsFetch('/api/cms/walks/bulk-operation', {
        method: 'POST',
        body: JSON.stringify({
          operation_type: operation,
          walk_ids: selectedWalks,
          parameters
        }),
      });

      if (response.ok) {
        setSelectedWalks([]);
        setShowBulkActions(false);
        fetchWalks();
      } else {
        console.error('Failed to perform bulk operation');
      }
    } catch (error) {
      console.error('Error performing bulk operation:', error);
    }
  };

  const handleDeleteWalk = async (walkId) => {
    if (!window.confirm('Are you sure you want to delete this walk? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await cmsFetch(`/api/cms/walks/${walkId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        fetchWalks();
        alert('Walk deleted successfully!');
      } else {
        console.error('Failed to delete walk');
        alert('Failed to delete walk. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting walk:', error);
      alert('Error deleting walk. Please try again.');
    }
  };

  const StatusBadge = ({ status }) => {
    const statusConfig = {
      draft: { color: 'gray', icon: Edit },
      pending_review: { color: 'yellow', icon: Clock },
      approved: { color: 'blue', icon: CheckCircle },
      published: { color: 'green', icon: CheckCircle },
      rejected: { color: 'red', icon: AlertTriangle },
      archived: { color: 'gray', icon: Archive }
    };

    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
        ${config.color === 'gray' ? 'bg-gray-100 text-gray-800' :
          config.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
          config.color === 'blue' ? 'bg-blue-100 text-blue-800' :
          config.color === 'green' ? 'bg-green-100 text-green-800' :
          'bg-red-100 text-red-800'}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status.replace('_', ' ')}
      </span>
    );
  };

  // Inline thumbnail of the optimized route + numbered stop markers.
  // Uses _static_map_url passed through by the /cms/walks endpoint when
  // present; falls back to lazy-fetch via /api/cms/walks/{id}/static-map
  // for walks the pre-generation script hasn't populated yet.
  //
  // Hover-expand uses a React-state + createPortal pattern instead of
  // Tailwind's group-hover because the parent table wrapper has
  // overflow-x-auto, which per CSS spec also clips overflow-y for
  // box-model consistency — a CSS-only absolutely-positioned overlay
  // gets clipped to roughly the row height regardless of z-index. The
  // portal renders into document.body and uses position:fixed with
  // getBoundingClientRect() coordinates, escaping every ancestor's
  // overflow context.
  const RouteMapCell = ({ walk, navigate }) => {
    const [url, setUrl] = useState(walk._static_map_url ?? null);
    const [fetched, setFetched] = useState(Boolean(walk._static_map_url));
    const [hovered, setHovered] = useState(false);
    const [hoverPos, setHoverPos] = useState({ left: 0, top: 0 });
    const thumbRef = useRef(null);
    const hasRoute = (walk.route_coordinates && walk.route_coordinates.length > 0);
    const stopCount = (walk.waypoints || []).length;
    const overflowCount = stopCount > 10 ? stopCount - 10 : 0;

    useEffect(() => {
      if (fetched || !hasRoute) return;
      let cancelled = false;
      (async () => {
        try {
          const resp = await cmsFetch(`/api/cms/walks/${walk.id}/static-map`);
          if (!cancelled && resp.ok) {
            const data = await resp.json();
            setUrl(data.url || null);
          }
        } catch {
          /* swallow — placeholder will render */
        } finally {
          if (!cancelled) setFetched(true);
        }
      })();
      return () => { cancelled = true; };
    }, [walk.id, fetched, hasRoute]);

    const handleMouseEnter = () => {
      if (!thumbRef.current) return;
      const rect = thumbRef.current.getBoundingClientRect();
      // Default: position overlay to the right of the thumbnail.
      // If it would overflow the viewport right edge, position to the left instead.
      const OVERLAY_W = 480;
      const OVERLAY_H = 360;
      const GAP = 8;
      let left = rect.right + GAP;
      if (left + OVERLAY_W > window.innerWidth - 16) {
        left = Math.max(16, rect.left - OVERLAY_W - GAP);
      }
      // Clamp top so the overlay stays in the viewport vertically.
      let top = rect.top;
      if (top + OVERLAY_H > window.innerHeight - 16) {
        top = Math.max(16, window.innerHeight - OVERLAY_H - 16);
      }
      setHoverPos({ left, top });
      setHovered(true);
    };
    const handleMouseLeave = () => setHovered(false);

    // Placeholder for walks with no route data — distinct visual signal
    if (!hasRoute) {
      return (
        <div
          onClick={() => navigate(`/cms/walks/${walk.id}/edit`)}
          className="w-[140px] h-[90px] rounded border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-[10px] text-gray-400 cursor-pointer hover:bg-gray-100 hover:border-gray-400 transition-colors"
          title="No route data — click to edit"
        >
          No route data
        </div>
      );
    }

    if (!fetched && !url) {
      return <div className="w-[140px] h-[90px] rounded bg-gray-100 animate-pulse" />;
    }

    if (!url) {
      return (
        <div
          onClick={() => navigate(`/cms/walks/${walk.id}/edit`)}
          className="w-[140px] h-[90px] rounded border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-[10px] text-gray-400 cursor-pointer hover:bg-gray-100 hover:border-gray-400 transition-colors"
          title="No route data — click to edit"
        >
          No route data
        </div>
      );
    }

    return (
      <div
        ref={thumbRef}
        className="relative inline-block"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <img
          src={url}
          alt={`Route map for ${walk.title}`}
          loading="lazy"
          width={140}
          height={90}
          onClick={() => navigate(`/cms/walks/${walk.id}/edit`)}
          className="w-[140px] h-[90px] rounded border border-gray-200 object-cover cursor-pointer hover:border-blue-400 transition-colors"
        />
        {overflowCount > 0 && (
          <span className="absolute top-1 right-1 bg-gray-800/85 text-white text-[10px] font-medium px-1.5 py-0.5 rounded pointer-events-none">
            +{overflowCount}
          </span>
        )}
        {/* Hover-expanded preview — portaled into document.body to escape
            the table wrapper's overflow clipping. */}
        {hovered && createPortal(
          <div
            style={{
              position: 'fixed',
              left: hoverPos.left,
              top: hoverPos.top,
              zIndex: 9999,
              pointerEvents: 'none',
            }}
          >
            <img
              src={url}
              alt={`Expanded route map for ${walk.title}`}
              width={480}
              height={360}
              className="w-[480px] h-[360px] rounded-lg border border-gray-300 shadow-xl bg-white"
            />
          </div>,
          document.body
        )}
      </div>
    );
  };

  const WalkRow = ({ walk }) => {
    const isSelected = selectedWalks.includes(walk.id);

    return (
      <tr className={`${isSelected ? 'bg-blue-50' : 'bg-white'} hover:bg-gray-50`}>
        <td className="px-6 py-4 whitespace-nowrap">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedWalks([...selectedWalks, walk.id]);
              } else {
                setSelectedWalks(selectedWalks.filter(id => id !== walk.id));
              }
            }}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
        </td>
        <td className="px-6 py-4">
          <div className="flex items-start">
            {walk.main_image && (
              <img 
                src={walk.main_image} 
                alt={walk.title}
                className="w-12 h-12 rounded-lg object-cover mr-3"
              />
            )}
            <div>
              <button 
                onClick={() => navigate(`/cms/walks/${walk.id}/edit`)}
                className="text-sm font-medium text-blue-600 hover:text-blue-900 cursor-pointer text-left"
              >
                {walk.title}
              </button>
              <div className="text-sm text-gray-500 truncate max-w-xs">{walk.description}</div>
              <div className="flex items-center flex-wrap mt-1 gap-1">
                {(walk.categories || []).map(cat => (
                  <span key={cat} className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full">
                    {cat.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </span>
                ))}
                {(walk.tags || []).slice(0, 4).map(tag => (
                  <span key={tag} className="text-[10px] bg-teal-50 text-teal-700 px-1.5 py-0.5 rounded-full">
                    {tag.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </span>
                ))}
                {(walk.tags || []).length > 4 && (
                  <span className="text-[10px] text-gray-400">+{walk.tags.length - 4}</span>
                )}
                <span className="text-xs text-gray-500">{walk.region}</span>
                <span className="text-xs text-gray-300">•</span>
                <span className="text-xs text-gray-500">{walk.difficulty}</span>
                <span className="text-xs text-gray-300">•</span>
                <span className="text-xs text-gray-500" title="Last updated">
                  Updated {new Date(walk.updated_at).toLocaleDateString()}
                </span>
                {walk.featured && <Star className="w-3 h-3 text-yellow-500" />}
              </div>
            </div>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <StatusBadge status={walk.status} />
        </td>
        <td className="px-3 py-2 whitespace-nowrap">
          <RouteMapCell walk={walk} navigate={navigate} />
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          <span className="capitalize">{walk.walk_type.replace('_', ' ')}</span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          <div className="flex items-center">
            <MapPin className="w-3 h-3 mr-1" />
            {(walk.distance_meters / 1609.34).toFixed(1)} mi
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          <div className="flex items-center">
            <Star className="w-3 h-3 mr-1" />
            {walk.rating.toFixed(1)}
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate(`/cms/walks/${walk.id}/edit`)}
              className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-md transition-colors"
              title="Edit Walk"
            >
              <Edit className="w-5 h-5" />
            </button>
            <button
              onClick={() => window.open(`/walks/${walk.country || 'us'}/${walk.city || 'los-angeles'}/${walk.neighborhood || 'explore'}/${walk.slug}`, '_blank')}
              className="p-2 text-green-600 hover:text-green-900 hover:bg-green-50 rounded-md transition-colors"
              title="Preview Walk"
            >
              <ExternalLink className="w-5 h-5" />
            </button>
            <div className="relative">
              <select
                onChange={(e) => {
                  if (e.target.value === 'delete') {
                    handleDeleteWalk(walk.id);
                  } else if (e.target.value) {
                    handleStatusChange(walk.id, e.target.value);
                  }
                  e.target.value = '';
                }}
                className="text-gray-600 hover:text-gray-900 bg-transparent border-none cursor-pointer"
              >
                <option value="">Actions</option>
                <option value="pending_review">Submit for Review</option>
                <option value="approved">Approve</option>
                <option value="published">Publish</option>
                <option value="archived">Archive</option>
                <option value="delete">Delete</option>
              </select>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  if (!hasAccess) {
    return <CMSAuth onAuthenticated={() => setHasAccess(true)} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/cms/dashboard')}
                className="text-gray-600 hover:text-gray-900 flex items-center"
                title="Back to CMS Dashboard"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to CMS
              </button>
              <div className="border-l border-gray-300 pl-4">
                <h1 className="text-2xl font-bold text-gray-900">Walk Management</h1>
                <p className="text-sm text-gray-500">Manage and organize walk content</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/cms/dashboard')}
                className="text-gray-600 hover:text-gray-900 flex items-center px-3 py-2 rounded-lg border border-gray-300"
                title="CMS Dashboard"
              >
                <Home className="w-4 h-4 mr-2" />
                Dashboard
              </button>
              <button
                onClick={() => navigate('/cms/walks/create')}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Walk
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search walks..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10 w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="pending_review">Pending Review</option>
              <option value="approved">Approved</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>

            <select
              value={filters.walk_type}
              onChange={(e) => setFilters(prev => ({ ...prev, walk_type: e.target.value }))}
              className="rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Types</option>
              <option value="curated">Curated</option>
              <option value="community">Community</option>
              <option value="gps_tracked">GPS Tracked</option>
            </select>

            <select
              value={filters.difficulty}
              onChange={(e) => setFilters(prev => ({ ...prev, difficulty: e.target.value }))}
              className="rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Difficulties</option>
              <option value="Easy">Easy</option>
              <option value="Moderate">Moderate</option>
              <option value="Hard">Hard</option>
            </select>

            <select
              value={filters.region}
              onChange={(e) => setFilters(prev => ({ ...prev, region: e.target.value }))}
              className="rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Regions</option>
              <option value="Silver Lake">Silver Lake</option>
              <option value="Griffith Park">Griffith Park</option>
              <option value="Venice">Venice</option>
              <option value="Echo Park">Echo Park</option>
              <option value="Los Feliz">Los Feliz</option>
            </select>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedWalks.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-blue-800">
                {selectedWalks.length} walk{selectedWalks.length > 1 ? 's' : ''} selected
              </span>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleBulkOperation('publish')}
                  className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                >
                  Publish Selected
                </button>
                <button
                  onClick={() => handleBulkOperation('archive')}
                  className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700"
                >
                  Archive Selected
                </button>
                <button
                  onClick={() => setSelectedWalks([])}
                  className="bg-gray-300 text-gray-700 px-3 py-1 rounded text-sm hover:bg-gray-400"
                >
                  Clear Selection
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={selectedWalks.length === walks.length && walks.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedWalks(walks.map(w => w.id));
                        } else {
                          setSelectedWalks([]);
                        }
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Walk
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Route Map
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Distance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rating
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="mt-2 text-gray-500">Loading walks...</p>
                    </td>
                  </tr>
                ) : walks.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <div className="text-gray-500">
                        <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>No walks found</p>
                        <button
                          onClick={() => window.location.href = '/cms/walks/create'}
                          className="mt-2 text-blue-600 hover:text-blue-800"
                        >
                          Create your first walk
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  walks.map(walk => <WalkRow key={walk.id} walk={walk} />)
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.total_count > pagination.per_page && (
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Showing {((pagination.page - 1) * pagination.per_page) + 1} to{' '}
                  {Math.min(pagination.page * pagination.per_page, pagination.total_count)} of{' '}
                  {pagination.total_count} results
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page * pagination.per_page >= pagination.total_count}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CMSWalkManagement;